import MDEditor from "@uiw/react-md-editor";
import React, { useEffect, useRef, useState } from "react";
import { deletePaper, getAllPapers, savePapers, uploadPaper } from "../../api/PapersApi";
import ContainerCard from "../../common/CardContainer";
import ErrorDisplay from "../../common/ErrorDisplay";
import LoadingSpinner from "../../common/LoadingSpinner";
import { toast } from "../../common/notify";
import PageHeader from "../../common/PageHeader";
import { SynodPaper, SynodPapers } from "../../data/SynodPapers";
import { formatFileSize, SynodPapersContent } from "../dashboard/SynodPapersBlock";

/** Title suggestion from a file name: "Daily_Notice-Paper 11 Oct.pdf" -> "Daily Notice Paper 11 Oct" */
export function titleFromFileName(fileName: string): string {
  return fileName
    .replace(/\.pdf$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sameContent(a: SynodPapers | null, b: SynodPapers | null): boolean {
  if (!a || !b) {
    return a === b;
  }
  const key = (p: SynodPapers) => JSON.stringify([p.contentMarkdown, p.papers.map((x) => [x.id, x.title, x.visible])]);
  return key(a) === key(b);
}

const PapersAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<SynodPapers | null>(null);
  const [draft, setDraft] = useState<SynodPapers | null>(null);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getAllPapers()
      .then((papers) => {
        setSaved(papers);
        setDraft(papers);
      })
      .catch((e: Error) => setError(`Could not load synod papers: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  const dirty = !sameContent(saved, draft);

  function updatePaper(id: string, change: Partial<SynodPaper>) {
    setDraft((d) => d && { ...d, papers: d.papers.map((p) => (p.id === id ? { ...p, ...change } : p)) });
  }

  function movePaper(index: number, offset: number) {
    setDraft((d) => {
      if (!d) return d;
      const target = index + offset;
      if (target < 0 || target >= d.papers.length) return d;
      const papers = [...d.papers];
      [papers[index], papers[target]] = [papers[target], papers[index]];
      return { ...d, papers };
    });
  }

  function onFileChosen(chosen: File | null) {
    setFile(chosen);
    setUploadTitle(chosen ? titleFromFileName(chosen.name) : "");
  }

  async function onUpload() {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error("Only PDF files can be uploaded");
      return;
    }
    setUploading(true);
    try {
      const paper = await uploadPaper(file, uploadTitle);
      // the server has already saved the new paper first in the list; keep any unsaved edits to the rest
      setSaved((s) => s && { ...s, papers: [paper, ...s.papers] });
      setDraft((d) => d && { ...d, papers: [paper, ...d.papers] });
      onFileChosen(null);
      if (fileInput.current) fileInput.current.value = "";
      toast.success(`Uploaded "${paper.title}" — it is now showing on the home page`);
    } catch (e: any) {
      toast.error(`Upload failed: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  async function onSave() {
    if (!draft) return;
    setSaving(true);
    try {
      const result = await savePapers(draft);
      setSaved(result);
      setDraft(result);
      toast.success("Synod papers saved");
    } catch (e: any) {
      toast.error(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(paper: SynodPaper) {
    if (!window.confirm(`Delete "${paper.title}"?\n\nThe PDF is removed and its link stops working. This cannot be undone.`)) {
      return;
    }
    try {
      await deletePaper(paper.id);
      const remove = (p: SynodPapers | null) => p && { ...p, papers: p.papers.filter((x) => x.id !== paper.id) };
      setSaved(remove);
      setDraft(remove);
      toast.success(`Deleted "${paper.title}"`);
    } catch (e: any) {
      toast.error(`Delete failed: ${(e as Error).message}`);
    }
  }

  async function onCopyLink(paper: SynodPaper) {
    const link = window.location.origin + paper.url;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      window.prompt("Copy this link:", link);
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <PageHeader title="Synod Papers" subtitle="Upload notice papers and edit the Synod Papers block on the home page">
        {dirty ? <span className="text-muted small">Unsaved changes</span> : null}
        <button className="btn btn-primary" disabled={!dirty || saving} onClick={onSave}>
          {saving ? "Saving..." : "Save changes"}
        </button>
      </PageHeader>
      <ErrorDisplay error={error} />

      {draft ? (
        <div className="row">
          <div className="col-lg-7">
            <ContainerCard heading="Upload a PDF">
              <div className="mb-2">
                <label htmlFor="paperFile" className="form-label">
                  PDF file
                </label>
                <input
                  id="paperFile"
                  ref={fileInput}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="form-control"
                  disabled={uploading}
                  onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="mb-2">
                <label htmlFor="paperTitle" className="form-label">
                  Title shown on the home page
                </label>
                <input id="paperTitle" type="text" className="form-control" value={uploadTitle} disabled={uploading} onChange={(e) => setUploadTitle(e.target.value)} />
              </div>
              <button className="btn btn-secondary" disabled={!file || uploading} onClick={onUpload}>
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <p className="form-text mb-0">New papers are added to the top of the list and shown on the home page straight away.</p>
            </ContainerCard>

            <ContainerCard heading="Papers">
              {draft.papers.length === 0 ? (
                <p className="text-muted mb-0">No papers uploaded yet.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th className="text-center">Show</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {draft.papers.map((paper, index) => (
                        <tr key={paper.id} className={paper.visible ? undefined : "sl-row-muted"}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              aria-label={`Title for ${paper.fileName}`}
                              value={paper.title}
                              onChange={(e) => updatePaper(paper.id, { title: e.target.value })}
                            />
                            <div className="sl-papers-meta mt-1">
                              {paper.fileName} · {formatFileSize(paper.sizeBytes)}
                              {paper.uploadedAt ? ` · uploaded ${new Date(paper.uploadedAt).toLocaleString()}` : ""}
                            </div>
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Show ${paper.title} on the home page`}
                              checked={paper.visible}
                              onChange={(e) => updatePaper(paper.id, { visible: e.target.checked })}
                            />
                          </td>
                          <td className="text-nowrap text-end">
                            <button className="btn btn-light btn-sm" title="Move up" aria-label={`Move ${paper.title} up`} disabled={index === 0} onClick={() => movePaper(index, -1)}>
                              ↑
                            </button>
                            <button
                              className="btn btn-light btn-sm ms-1"
                              title="Move down"
                              aria-label={`Move ${paper.title} down`}
                              disabled={index === draft.papers.length - 1}
                              onClick={() => movePaper(index, 1)}
                            >
                              ↓
                            </button>
                            <a className="btn btn-light btn-sm ms-1" href={paper.url} target="_blank" rel="noreferrer">
                              Open
                            </a>
                            <button className="btn btn-light btn-sm ms-1" onClick={() => onCopyLink(paper)}>
                              Copy link
                            </button>
                            <button className="btn btn-outline-danger btn-sm ms-1" onClick={() => onDelete(paper)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="form-text mb-0">Order, titles and Show take effect when you save. Links keep working for hidden papers; deleting a paper breaks its link.</p>
            </ContainerCard>

            <ContainerCard heading="Note">
              <p className="form-text mt-0">Shown above the list of papers. Leave empty for no note.</p>
              <div data-color-mode="light">
                <MDEditor value={draft.contentMarkdown} onChange={(value) => setDraft((d) => d && { ...d, contentMarkdown: value ?? "" })} preview="edit" height={220} />
              </div>
            </ContainerCard>
          </div>

          <div className="col-lg-5">
            <ContainerCard heading="Preview: Synod Papers">
              <SynodPapersContent papers={{ ...draft, papers: draft.papers.filter((p) => p.visible) }} />
            </ContainerCard>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PapersAdmin;
